import AnimatedEditor from './AnimatedEditor';
import LiveTimestamp from './LiveTimestamp';
import { MonoCTA } from './MonoCTA';
import { DiaryLine } from './DiaryLine';
import { APP_STORE_URL, MAC_APP_STORE_URL } from '@/constants';

const JVTECHTEA_URL = 'https://youtube.com/@jvtechtea';

function MacFramedEditor() {
  return (
    <div className="overflow-hidden rounded-lg border border-border/60 bg-background">
      <div className="flex items-center gap-3 border-b border-border/40 px-4 py-2.5">
        <div className="flex items-center gap-1.5">
          <span aria-hidden className="block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: '#ff5f57' }} />
          <span aria-hidden className="block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: '#febc2e' }} />
          <span aria-hidden className="block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: '#28c840' }} />
        </div>
        <span className="font-mono text-xs text-foreground/60">today</span>
      </div>
      <AnimatedEditor />
    </div>
  );
}

export function Hero() {
  return (
    <div className="lg:grid lg:grid-cols-[minmax(0,11fr)_minmax(0,10fr)] lg:items-center lg:gap-14">
      <div>
      <DiaryLine time={<span className="hidden sm:inline"><LiveTimestamp captureOn="mount" /></span>}>
        <h1
          className="-ml-14 max-w-[15ch] font-serif text-balance text-[clamp(2.5rem,6vw,4.75rem)] leading-[1.02] text-foreground sm:ml-0 lg:text-[clamp(2.5rem,3.6vw,3.5rem)] motion-safe:animate-[stagger-in_600ms_cubic-bezier(0.2,0.9,0.2,1)_both]"
        >
          Notes that remember <em>when</em>.
          <span className="sr-only"> Timestamped notes app - free, offline, no signup.</span>
        </h1>
      </DiaryLine>

      <DiaryLine className="mt-8">
        <p className="-ml-14 w-[calc(100vw_-_3rem)] text-pretty text-base leading-relaxed text-muted-foreground sm:ml-0 sm:w-auto sm:max-w-[52ch] sm:text-lg motion-safe:animate-[stagger-in_600ms_120ms_cubic-bezier(0.2,0.9,0.2,1)_both]">
          Notes with automatic timestamps.
          <br />
          Minimalistic distraction-free experience.
          <br />
          Free, offline, no signup.
        </p>
      </DiaryLine>

      <DiaryLine className="mt-10">
        <div className="-ml-14 flex w-[calc(100vw_-_3rem)] flex-col items-center sm:ml-0 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center sm:gap-4 motion-safe:animate-[stagger-in_600ms_220ms_cubic-bezier(0.2,0.9,0.2,1)_both]">
          {/* Mobile and lg (narrow hero column): width fits the badges row so
              "Try on web" matches that combined width. Tablet: dissolves via
              `contents` so all three CTAs sit in one full-width row. */}
          <div className="flex w-fit flex-col items-stretch gap-3 sm:contents lg:flex">
            <MonoCTA
              to="/diaries"
              size="hero"
              className="w-full min-h-[46px] h-[46px] px-6 text-sm sm:w-auto"
            >
              Try on web →
            </MonoCTA>
            <div className="flex flex-row items-center gap-3 sm:contents lg:flex">
              <a
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex shrink-0 items-center rounded transition-opacity hover:opacity-85 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
              >
                <img
                  src="/images/app-store-badge.svg"
                  alt="Download on the App Store"
                  width={120}
                  height={40}
                  className="h-[46px] w-auto"
                />
              </a>
              <a
                href={MAC_APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex shrink-0 items-center rounded transition-opacity hover:opacity-85 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
              >
                <img
                  src="/images/mac-app-store-badge.svg"
                  alt="Download on the Mac App Store"
                  width={156}
                  height={40}
                  className="h-[46px] w-auto"
                />
              </a>
            </div>
          </div>
        </div>
      </DiaryLine>

      {/* Quote provided directly by Joshua Vergara (JV Tech Tea), a paying
          customer, 2026-07-21, lightly condensed (with his blessing assumed
          by the site owner) to fit two lines on mobile. Original: "I just
          learned about interstitial journaling, and have become a big fan of
          it -- mainly because of notetime. Simple task made with good
          implementation. I appreciate it." */}
      <figure className="mt-5 flex items-start gap-3 sm:ml-16 motion-safe:animate-[stagger-in_600ms_260ms_cubic-bezier(0.2,0.9,0.2,1)_both]">
        <img
          src="/images/jv-tech-tea.jpg"
          alt=""
          width={40}
          height={40}
          className="mt-0.5 h-10 w-10 shrink-0 rounded-full border border-border/60"
        />
        <div className="min-w-0">
          <blockquote className="max-w-[46ch] text-pretty text-sm leading-relaxed text-muted-foreground">
            &ldquo;&hellip;learned about interstitial journaling and became a big fan of it,
            mainly because of notetime&rdquo;
          </blockquote>
          <figcaption className="mt-1 font-mono text-xs text-muted-foreground">
            <span className="text-foreground">Joshua Vergara</span>,{' '}
            <a
              href={JVTECHTEA_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="underline decoration-foreground/30 underline-offset-2 transition-colors hover:decoration-foreground/60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            >
              JV Tech Tea
              <img
                src="/images/youtube.svg"
                alt="on YouTube"
                width={14}
                height={10}
                className="ml-1.5 inline-block h-2.5 w-auto align-baseline"
              />
            </a>
          </figcaption>
        </div>
      </figure>
      </div>

      {/* Single editor instance: full-bleed on mobile, aligned to the diary
          content column on sm (pl-16 mirrors the 56px gutter + 8px gap), and
          the hero's right grid column on lg. */}
      <div className="mt-10 sm:pl-16 lg:mt-0 lg:pl-0">
        <div className="sm:max-w-[640px] lg:max-w-none motion-safe:animate-[stagger-in_800ms_280ms_cubic-bezier(0.2,0.9,0.2,1)_both]">
          <MacFramedEditor />
        </div>
      </div>
    </div>
  );
}
